import { format } from "date-fns";

/** Toutes les valeurs acceptées dans l'URL, tous onglets confondus. */
export const REPORTING_PERIOD_VALUES = [
  "week",
  "last4w",
  "last30",
  "month",
  "quarter",
  "last12m",
  "year",
  "all",
] as const;

export type ReportingPeriod = (typeof REPORTING_PERIOD_VALUES)[number];

/** « Depuis le début » n'a pas de date de départ : seul le Portfolio le propose. */
export type BoundedPeriod = Exclude<ReportingPeriod, "all">;

export interface PeriodPreset {
  value: ReportingPeriod;
  label: string;
  /** Preset sélectionné à l'arrivée. Sans drapeau, c'est le premier de la liste. */
  isDefault?: boolean;
}

/**
 * ⚠ Les fenêtres GLISSANTES ouvrent chaque page, les unités calendaires restent en second.
 *
 * Une unité calendaire redémarre à zéro : le 2 janvier, « Cette année » n'a que deux jours
 * d'activité et la page affiche des zéros qu'on lit comme une panne. Une fenêtre glissante porte
 * toujours la même durée, donc elle se compare à elle-même d'un jour sur l'autre. Les unités
 * calendaires ne disparaissent pas pour autant — elles répondent à la question comptable
 * « où en est ce mois-ci ? », qu'une fenêtre glissante ne sait pas poser.
 */
const LAST_4_WEEKS: PeriodPreset = { value: "last4w", label: "4 dernières semaines" };
const LAST_30_DAYS: PeriodPreset = { value: "last30", label: "30 derniers jours" };
const LAST_12_MONTHS: PeriodPreset = { value: "last12m", label: "12 derniers mois" };
const THIS_MONTH: PeriodPreset = { value: "month", label: "Ce mois-ci" };
const THIS_QUARTER: PeriodPreset = { value: "quarter", label: "Ce trimestre" };
const THIS_YEAR: PeriodPreset = { value: "year", label: "Cette année" };
/** Tout l'historique : ce que voient les clients sur le Portfolio (TECH-1182). */
const ALL_TIME: PeriodPreset = { value: "all", label: "Depuis le début" };

/** Le portefeuille se lit sur un an : le mois est trop court pour juger un stock de dossiers. */
const PORTFOLIO_PERIODS: PeriodPreset[] = [LAST_12_MONTHS, THIS_MONTH, THIS_QUARTER, THIS_YEAR, ALL_TIME];

/** Le funnel se lit au mois : en deçà, la population signataire est trop mince pour un taux. */
const SIGNATURE_PERIODS: PeriodPreset[] = [LAST_30_DAYS, THIS_MONTH, THIS_QUARTER, THIS_YEAR];

/**
 * Le pilotage se lit sur quatre semaines : l'horizon d'une équipe ops. Ni le mois calendaire
 * (qui coupe une semaine en deux) ni l'année (qui noie la semaine en cours).
 */
const OPS_PERIODS: PeriodPreset[] = [LAST_4_WEEKS, THIS_QUARTER, LAST_12_MONTHS];

/** « Suivi des vols » raisonne en vols du mois écoulé, pas en mois calendaire. */
const FLIGHT_PERIODS: PeriodPreset[] = [LAST_30_DAYS, THIS_QUARTER, THIS_YEAR];

const COMMERCIAL_PERIOD_VALUES = ["week", "month", "quarter"] as const;

export type CommercialPeriod = (typeof COMMERCIAL_PERIOD_VALUES)[number];

/**
 * « Financier » : ici les onglets choisissent la MAILLE, pas une fenêtre calendaire — d'où
 * « Par mois » et non « Ce mois-ci ». Le mart commercial n'existe qu'en semaine / mois /
 * trimestre (ni jour, ni année), et « Ce mois-ci » à la maille mois ne produirait qu'une seule
 * barre : la page se lit sur plusieurs périodes glissantes.
 */
const COMMERCIAL_PERIODS: PeriodPreset[] = [
  { value: "week", label: "Par semaine" },
  // Le mois ouvre la page sans casser l'ordre chronologique des onglets.
  { value: "month", label: "Par mois", isDefault: true },
  { value: "quarter", label: "Par trimestre" },
];

/**
 * Onglets par sous-page — le PREMIER preset fait office de défaut. `period` reste absent de
 * l'URL tant que l'utilisateur n'a rien choisi, ce qui laisse chaque page appliquer le sien.
 *
 * « Recouvrement » n'a AUCUN onglet : son mart couvre l'historique complet et l'encours est une
 * photo à date. Afficher des onglets sans effet serait pire que de ne pas en afficher.
 */
const PERIODS_BY_ROUTE: Record<string, PeriodPreset[]> = {
  "/reporting": PORTFOLIO_PERIODS,
  "/reporting/vols": FLIGHT_PERIODS,
  "/reporting/signatures": SIGNATURE_PERIODS,
  "/reporting/pilotage": OPS_PERIODS,
  "/reporting/commercial": COMMERCIAL_PERIODS,
  "/reporting/recouvrement": [],
};

/** Sous-page sans preset connu : une fenêtre glissante courte, jamais une unité calendaire. */
const FALLBACK_PERIOD: BoundedPeriod = "last30";

export function periodsForRoute(pathname: string): PeriodPreset[] {
  return PERIODS_BY_ROUTE[pathname] ?? [];
}

/**
 * Les onglets diffèrent d'une page à l'autre : une période venue de l'URL (ou d'une autre
 * sous-page) qui n'existe pas ici retombe sur le défaut de la page plutôt que de casser.
 */
export function resolvePeriod(pathname: string, period?: ReportingPeriod): ReportingPeriod {
  const presets = periodsForRoute(pathname);
  if (period && presets.some((preset) => preset.value === period)) {
    return period;
  }
  const fallback = presets.find((preset) => preset.isDefault) ?? presets[0];
  return fallback?.value ?? FALLBACK_PERIOD;
}

/** Pour les pages sans « Depuis le début » : leurs requêtes exigent des bornes. */
export function resolveBoundedPeriod(pathname: string, period?: ReportingPeriod): BoundedPeriod {
  const resolved = resolvePeriod(pathname, period);
  return resolved === "all" ? FALLBACK_PERIOD : resolved;
}

/**
 * Même résolution que les onglets, re-typée au grain du mart commercial. La page la partage
 * avec la shell : deux résolutions séparées avaient fini par se contredire — onglet « semaine »
 * surligné, données mensuelles affichées.
 */
export function resolveCommercialPeriod(period?: ReportingPeriod): CommercialPeriod {
  const resolved = resolvePeriod("/reporting/commercial", period);
  return COMMERCIAL_PERIOD_VALUES.find((value) => value === resolved) ?? "month";
}

export type ReportGranularity = "yearly" | "monthly" | "quarterly" | "weekly";

/** Les endpoints agrégés historiques ne connaissent que les unités calendaires. */
export type CalendarPeriod = "week" | "month" | "quarter" | "year";

const GRANULARITY_BY_PERIOD = {
  week: "weekly",
  month: "monthly",
  quarter: "quarterly",
  year: "yearly",
} as const satisfies Record<CalendarPeriod, ReportGranularity>;

/**
 * Body sent to the reporting endpoints. The API resolves the calendar unit
 * containing `startingDate` and, with `previousPeriod`, also computes the same
 * unit one year earlier (populates the response's `previous` field).
 * `tenantId` / `travelAgencyCompanyId` narrow the scope (Upclaim filters).
 */
export interface ReportQuery {
  startingDate: string;
  granularity: ReportGranularity;
  previousPeriod: true;
  tenantId?: string;
  travelAgencyCompanyId?: string;
}

export function periodToQuery(period: CalendarPeriod, now: Date = new Date()): ReportQuery {
  return {
    startingDate: format(now, "yyyy-MM-dd"),
    granularity: GRANULARITY_BY_PERIOD[period],
    previousPeriod: true,
  };
}
